import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { WizardData } from '../types';
import { INITIAL_WIZARD_DATA } from '../types';
import {
  STEPS,
  ONBOARDING_BASE_PATH,
  getStepIndexByPath,
  getStepPathByIndex,
} from '../constants';
import { isStepValid, canAccessStep } from '../utils';

const STORAGE_KEY = 'onboarding-wizard-data';

/**
 * Load wizard data from sessionStorage
 */
function loadFromStorage(): WizardData {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Restore data but reset file references (can't be serialized)
      return {
        ...INITIAL_WIZARD_DATA,
        ...parsed,
        // Design assets are stored with URLs, so they persist
      };
    }
  } catch (e) {
    console.warn('Failed to load wizard data from storage:', e);
  }
  return INITIAL_WIZARD_DATA;
}

/**
 * Save wizard data to sessionStorage
 */
function saveToStorage(data: WizardData): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save wizard data to storage:', e);
  }
}

/**
 * Clear wizard data from sessionStorage
 * Wrapped in try-catch for SSR/test environments where sessionStorage may not exist
 */
export function clearWizardStorage(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently ignore - storage may not be available in SSR/tests
  }
}

/**
 * Custom hook for wizard state management with URL sync and persistence
 */
export function useWizardState() {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize data from storage
  const [data, setData] = useState<WizardData>(loadFromStorage);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get current step from URL
  const pathSegments = location.pathname.split('/');
  const currentPath = pathSegments[pathSegments.length - 1] || 'customer';
  const currentStep = getStepIndexByPath(currentPath);

  // Persist data changes to sessionStorage
  useEffect(() => {
    saveToStorage(data);
  }, [data]);

  // Navigation guard: redirect if trying to access invalid step
  useEffect(() => {
    if (!canAccessStep(currentStep, data)) {
      // Find the first invalid step and redirect there
      for (let i = 0; i < currentStep; i++) {
        if (!isStepValid(i, data)) {
          navigate(`${ONBOARDING_BASE_PATH}/${getStepPathByIndex(i)}`, {
            replace: true,
          });
          return;
        }
      }
    }
  }, [currentStep, data, navigate]);

  // Update data
  const updateData = useCallback((updates: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Reset data
  const resetData = useCallback(() => {
    setData(INITIAL_WIZARD_DATA);
    clearWizardStorage();
  }, []);

  // Navigate to step by index
  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < STEPS.length) {
        navigate(`${ONBOARDING_BASE_PATH}/${getStepPathByIndex(stepIndex)}`);
      }
    },
    [navigate]
  );

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      goToStep(currentStep + 1);
    }
  }, [currentStep, goToStep]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  // Check if current step is valid
  const isCurrentStepValid = isStepValid(currentStep, data);

  // Calculate progress
  const progress = Math.round((currentStep / (STEPS.length - 1)) * 100);

  // Check if on last step
  const isLastStep = currentStep === STEPS.length - 1;

  return {
    // Data
    data,
    updateData,
    resetData,

    // Navigation
    currentStep,
    goToStep,
    goToNextStep,
    goToPreviousStep,

    // Validation
    isCurrentStepValid,
    canAccessStep: (step: number) => canAccessStep(step, data),
    isStepCompleted: (step: number) => isStepValid(step, data),

    // UI state
    progress,
    isLastStep,
    isSubmitting,
    setIsSubmitting,
  };
}
